<template>
  <div class="projects-po-balance-cal-view">
    <div class="row">
      <div class="small-7 medium-7 large-7 columns" />
      <div class="small-3 medium-3 large-3 columns align-right">
        <label class="amount uppercase">{{ $t('amount') }}</label>
      </div>
      <div class="small-2 medium-2 large-2 columns align-right">
        <label class="percentage uppercase">{{ $t('percentage') }}</label>
      </div>
    </div>

    <div class="row odd-row body-row">
      <div class="small-7 medium-7 large-7 columns">
        <label class="align-left row-title">{{ amountLabel }}</label>
      </div>
      <div class="small-3 medium-3 large-3 columns align-right">
        <label class="amount">{{ purchaseAmount | formatCurrency({currencyCode}) }}</label>
      </div>
      <div class="small-2 medium-2 large-2 columns align-right">
        <label class="percentage">--</label>
      </div>
    </div>

    <div
      v-if="deductionAmount > 0"
      class="row body-row"
    >
      <div class="small-7 medium-7 large-7 columns">
        <label class="align-left row-title">{{ $t('project.deduction.label') }}</label>
      </div>
      <div class="small-3 medium-3 large-3 columns align-right">
        <label class="amount">{{ deductionAmount | formatCurrency({currencyCode}) }}</label>
      </div>
      <div class="small-2 medium-2 large-2 columns align-right">
        <label class="percentage">--</label>
      </div>
    </div>

    <div class="row body-row">
      <div class="small-7 medium-7 large-7 columns">
        <label class="align-left row-title">{{ $t('project.submission.previousAmount') }}</label>
      </div>
      <div class="small-3 medium-3 large-3 columns align-right">
        <label class="amount">{{ previousTotalAmount | formatCurrency({currencyCode}) }}</label>
      </div>
      <div class="small-2 medium-2 large-2 columns align-right">
        <label class="percentage">{{ previousAmountPct }}%</label>
      </div>
    </div>

    <div class="row odd-row body-row">
      <div class="small-7 medium-7 large-7 columns">
        <label
          class="align-left row-title"
          :class="{'label-with-hint required-flag': !readonly}"
        >
          {{ $t('project.submission.currentAmount') }}
        </label>
      </div>
      <div
        v-if="readonly"
        class="small-3 medium-3 large-3 columns align-right"
      >
        <label class="amount">{{ currentAmount | formatCurrency({currencyCode}) }}</label>
      </div>
      <div
        v-else
        class="small-3 medium-3 large-3 columns align-right"
      >
        <div class="no-margin-right">
          <p class="amount-ccy">
            {{ currencyCode || $globalProps.getCurrency() }}
          </p>
          <input
            v-hkldinput
            v-fusioninputcheck="!showInvoiceModule && {
              type: 'decimal',
              pattern: (val) => (Number((val || '').replace(/,/g, '')) || 0) >= 0
            }"
            :disabled="showInvoiceModule"
            type="text"
            :value="submission.completedAmountText || submission.completedAmount"
            class="amount-input"
            :class="{'error-highlight' : !showInvoiceModule && highlightInvalid }"
            novalidate
            @change="event => {
              updateField('completedAmountText', event.target.value, 'string'),
              updateField('completedAmount', event.target.value),
              $emit('clear:errorHighlight')
            }"
          >
        </div>
      </div>
      <div
        class="small-2 medium-2 large-2 columns align-right"
        :class="{'completion-percentage': !readonly}"
      >
        <label class="percentage">{{ currentAmountPct }}%</label>
      </div>
    </div>

    <div class="row body-row">
      <div
        class="small-7 medium-7 large-7 columns"
        :class="{'label-with-hint-balance': !readonly}"
      >
        <label class="align-left row-title">{{ remainingBalanceLabel }}</label>
        <p
          v-if="!readonly"
          class="hint"
        >
          {{ remainingBalanceHint }}
        </p>
      </div>
      <div
        class="small-3 medium-3 large-3 columns"
        :class="{'balance-percentage': !readonly}"
      >
        <label class="amount">{{ remainingBalance | formatCurrency({currencyCode}) }}</label>
      </div>
      <div
        class="small-2 medium-2 large-2 columns align-right"
        :class="{'balance-percentage': !readonly}"
      >
        <label class="percentage">{{ remainingBalancePct }}%</label>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import { submissionStatus, completionType } from '@/utils/locale'
import mutationTypes from '@/store/mutation-types'
import amountUtil, { calculatePercentage } from '@/utils/amountUtil'
import { isRetentionPaymentSr } from '@/modules/projects/submission-request/utils'
import { useStoreGetters } from '@/store/index'
import { unref, computed } from 'vue-demi'
import lodash from 'lodash'

export default {
  name: 'PoBalanceCalculation',
  props: {
    readonly: {
      type: Boolean,
      default: false,
    },
    srIndex: {
      type: Number,
      default: 0,
    },
    submission: Object,
    highlightInvalid: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    const { backEndRegionalEnv } = useStoreGetters('regions', ['backEndRegionalEnv'])
    const showInvoiceModule = computed(() => {
      return lodash.get(unref(backEndRegionalEnv), 'moduleSwitch.submissionInvoiceModule')
    })

    return {
      showInvoiceModule,
    }
  },

  methods: {
    ...mapMutations('projects', {
      updateProjectField: mutationTypes.UPDATE_PATH_FIELD,
    }),
    updateField (fieldName, value, type = 'number') {
      const path = ['submissions', this.srIndex, fieldName]
      let newValue = value
      if (type === 'number') {
        newValue = value ? Number(value) : null
      }
      this.updateProjectField({ path, newValue })
    },
  },

  computed: {
    ...mapGetters('projects', [
      'currentProject', 'typeLabel', 'purchase',
    ]),

    purchaseAmount () {
      if (!this.currentProject) return 0
      return amountUtil.format(this.purchase.amount)
    },

    currencyCode () {
      return (this.purchase || {}).currencyCode
    },
    revisedPoAmount () {
      if (!this.currentProject) return 0
      return amountUtil.format(this.purchaseAmount - this.deductionAmount)
    },

    amountLabel () {
      return this.$t(`project.purchase.amount`, {type: this.typeLabel})
    },

    deductionAmount () {
      return amountUtil.format(this.submission.deductionAmount || 0)
    },

    previousTotalAmount () {
      if (!this.currentProject && parseFloat(this.currentProject.completedAmount)) return 0
      if (!this.readonly) {
        return amountUtil.format(this.currentProject.completedAmount)
      }

      let previousAmount = 0
      const submissions = this.currentProject.submissions || []
      for (let i = this.srIndex + 1; i < submissions.length; i += 1) {
        if (submissionStatus.APPR === submissions[i].status) {
          previousAmount = amountUtil.format(parseFloat(previousAmount) + parseFloat(submissions[i].completedAmount))
        }
      }

      return amountUtil.format(previousAmount)
    },

    previousAmountPct () {
      if (!this.previousTotalAmount) return 0
      return calculatePercentage(this.previousTotalAmount, this.revisedPoAmount)
    },

    currentAmount () {
      if (!this.submission) return 0
      return amountUtil.format(this.submission.completedAmount)
    },
    currentAmountPct () {
      if (!(this.currentProject && this.currentAmount)) return 0
      return calculatePercentage(this.currentAmount, this.revisedPoAmount)
    },
    remainingBalanceHint () {
      return isRetentionPaymentSr(this.submission.completionType) && (this.remainingBalance > 0)
        ? this.$t('hint.remainingBalanceToBeCanceled')
        : this.$t('hint.uponApprSubmission')
    },

    remainingBalanceLabel () {
      const isAcceptedFinalPayment = this.readonly && this.submission &&
        this.submission.status === submissionStatus.APPR &&
        this.submission.completionType === completionType.FINAL_PAYMENT &&
        this.remainingBalance > 0
      return this.$t(isAcceptedFinalPayment
        ? 'project.submission.remainingBalanceDeducted'
        : 'project.submission.remainingBalance')
    },

    remainingBalance () {
      if (!this.currentProject) return 0
      return amountUtil.format(
        this.purchaseAmount - this.deductionAmount - this.previousTotalAmount - this.currentAmount,
      )
    },
    remainingBalancePct () {
      if (!this.currentProject) return 0
      return calculatePercentage(this.remainingBalance, this.revisedPoAmount)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/project/style';

.projects-po-balance-cal-view {
  border: 1px solid #e1e1e1;
  border-bottom: 0;
  .text-field {
    margin: 0;
    label {
      margin-top: 0;
    }
    pre p {
      margin-bottom: 0;
    }
  }
  .label-with-hint {
    margin-top: 14px;
    margin-bottom: 5px;
  }
  .label-with-hint-balance {
    label {
      margin-top: 10px;
      margin-bottom: 5px;
    }
    p {
      margin-bottom: 10px;
    }
  }
  .completion-percentage {
    margin: 10px 0;
  }
  .balance-percentage {
    margin: 11px 0;
  }
}
.uppercase {
  text-transform: uppercase;
}
.row {
  border-bottom: 1px solid #e1e1e1;
  div label {
    width: 100%;
    margin: 10px 0;
  }
  .columns {
    border-right: 1px solid #e1e1e1;
    margin-right: 0;
    padding: 0 10px;
  }
  .columns:last-child {
    border-right: 0;
  }
}
.percentage {
  border-right:0;
}
.body-row label {
  font-weight: normal;
}
.amount-ccy {
  display: inline;
  margin-top: 23px;
  margin-bottom: 5px;
  font-size: 14px;
  line-height: 1.43;
  text-align: left;
  color: #0C3A58;
}

.odd-row {
  background-color: #f8f8f8;
}

.align-left {
  text-align: left
}

.align-right,
.amount,
.percentage {
  text-align: right;
}
.hint {
  margin-bottom: 0;
}
.amount-input {
  margin-top: 10px;
  text-align: right;
  clear: both;
  display: inline-block;
  width: 80%;
}

.no-margin-right {
  margin-right: 0;
}

</style>
